import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { User, AuthState } from '../types';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
  });

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Get current authenticated user from Cognito
      const user = await getCurrentUser();

      if (user) {
        // Get user attributes
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          const attributes = idToken.payload;

          // Check if user belongs to Superadmin group
          const groups = (attributes['cognito:groups'] as string[]) || [];

          if (!groups.includes('Superadmin')) {
            // User is not in Superadmin group - deny access
            await signOut();
            throw new Error('Access denied. Only Superadmin users can access this panel.');
          }

          const userData: User = {
            email: attributes.email as string,
            name: attributes.name as string,
            userId: attributes.sub as string,
            role: (attributes['custom:role'] as User['role']) || 'super_admin',
            lastLogin: new Date().toISOString(),
          };

          setAuthState({
            isAuthenticated: true,
            user: userData,
            loading: false,
            error: null,
          });
        } else {
          throw new Error('No valid session found');
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Sign in with Cognito
      await signIn({ username: email, password });

      // Refresh auth state after successful login
      // This will check for Superadmin group membership and throw error if not authorized
      await checkAuth();
    } catch (error: any) {
      console.error('Login error:', error);

      // Sign out the user if they're not authorized
      try {
        await signOut();
      } catch (signOutError) {
        console.error('Sign out error:', signOutError);
      }

      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Login failed',
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      await signOut();

      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Logout failed',
      }));
      throw error;
    }
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
